import {
  ChevronRightIcon,
  InfoCircledIcon,
  LockClosedIcon,
  LockOpen1Icon,
} from '@radix-ui/react-icons';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Badge } from '../../../primitives/badge';
import { Button } from '../../../primitives/button';
import { ButtonGroup } from '../../../primitives/button-group';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../primitives/collapsible';
import { Input } from '../../../primitives/input';
import { InputNumber } from '../../../primitives/input-number';
import { Separator } from '../../../primitives/separator';
import { SliderWithBadge } from '../../../primitives/slider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../primitives/tooltip';
import { ComboboxModel } from '../../combobox/combobox-model';
import { LabelWithoutRef } from '../../label/label-without-ref';

import { cn } from '../../../utils/cn';
import { getRangeColor } from '../../../utils/get-range-color';

import { bucketsToRanges } from './utils/buckets-to-ranges';
import { normalizeRanges } from './utils/normalize-ranges';
import { rangesToBuckets } from './utils/ranges-to-buckets';

import { InputId } from '../input-id';
import { BucketButton } from './components/bucket-button';

import { type BucketRange } from './_types';
import { GRID_QUADRANT_ORDER, QUADRANTS } from './quadrants.const';

const BUCKET_TYPE = {
  simple: 'simple',
  advanced: 'advanced',
} as const;

const TOTAL_BUCKETS = 100;
const MIN_SIMPLE_RANGES = 2;
const MAX_SIMPLE_RANGES = 100;
const MIN_ADVANCED_RANGES = 1;

function clampRangeCount(value: number): number {
  return Math.min(MAX_SIMPLE_RANGES, Math.max(MIN_SIMPLE_RANGES, Math.floor(value)));
}

function buildSimpleRanges(rangeCount: number): BucketRange[] {
  const count = clampRangeCount(rangeCount);
  const baseSize = Math.floor(TOTAL_BUCKETS / count);
  const lastSize = TOTAL_BUCKETS - baseSize * (count - 1);

  const ranges: BucketRange[] = [];
  let from = 0;
  for (let index = 0; index < count; index++) {
    const size = index === count - 1 ? lastSize : baseSize;
    const to = from + size - 1;
    ranges.push({ from, to });
    from = to + 1;
  }
  return ranges;
}

function sanitizeRange(range: BucketRange): BucketRange {
  const from = Math.max(0, Math.min(TOTAL_BUCKETS - 1, range.from));
  const to = Math.max(0, Math.min(TOTAL_BUCKETS - 1, range.to));
  return { from: Math.min(from, to), to: Math.max(from, to) };
}

function fillGapsAsRanges(ranges: BucketRange[]): BucketRange[] {
  const sanitized = ranges
    .map(sanitizeRange)
    .sort((a, b) => (a.from === b.from ? a.to - b.to : a.from - b.from));

  if (sanitized.length === 0) return [];

  const result: BucketRange[] = [];
  let cursor = 0;

  for (const range of sanitized) {
    const start = Math.max(range.from, cursor);
    if (start > cursor) result.push({ from: cursor, to: start - 1 });
    if (start <= range.to) {
      result.push({ from: start, to: range.to });
      cursor = range.to + 1;
    }
  }

  if (cursor <= TOTAL_BUCKETS - 1) result.push({ from: cursor, to: TOTAL_BUCKETS - 1 });
  return result;
}

function subtractRange(source: BucketRange, cut: BucketRange): BucketRange[] {
  if (cut.to < source.from || cut.from > source.to) return [source];
  const parts: BucketRange[] = [];
  if (cut.from > source.from) parts.push({ from: source.from, to: cut.from - 1 });
  if (cut.to < source.to) parts.push({ from: cut.to + 1, to: source.to });
  return parts;
}

function applyAdvancedSelectedRange(
  currentRanges: BucketRange[],
  selected: BucketRange,
): BucketRange[] {
  const selectedRange = sanitizeRange(selected);
  const baseRanges = currentRanges.map(sanitizeRange);
  const keptRanges = baseRanges.flatMap((range) => subtractRange(range, selectedRange));
  return fillGapsAsRanges([...keptRanges, selectedRange]);
}

function buildRandomRanges(rangeCount: number): BucketRange[] {
  const count = clampRangeCount(rangeCount);
  if (count === TOTAL_BUCKETS) {
    return Array.from({ length: TOTAL_BUCKETS }, (_, bucket) => ({ from: bucket, to: bucket }));
  }

  const cutPoints = Array.from({ length: TOTAL_BUCKETS - 1 }, (_, index) => index + 1);
  for (let index = cutPoints.length - 1; index > 0; index--) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [cutPoints[index], cutPoints[randomIndex]] = [cutPoints[randomIndex], cutPoints[index]];
  }

  const sortedCuts = cutPoints.slice(0, count - 1).sort((a, b) => a - b);
  const boundaries = [0, ...sortedCuts, TOTAL_BUCKETS];

  return Array.from({ length: count }, (_, index) => ({
    from: boundaries[index],
    to: boundaries[index + 1] - 1,
  }));
}

export type VariantSchemaValue = {
  ranges: BucketRange[];
  salt?: string;
  saltBase: string;
  type?: keyof typeof BUCKET_TYPE;
};

export interface InputVariantSchemaProps {
  disabled?: boolean;
  value?: VariantSchemaValue;
  onChange: (value: VariantSchemaValue) => void;
  saltBaseOptions?: { attributeName: string; codeDataType?: string }[];
  onSaltBaseChange?: (saltBase: string) => void;
}

export interface InputVariantSchemaRef {
  // Cancels any in-progress selection (click-range anchor or drag) without committing changes.
  cancelRange: VoidFunction;
}

export const InputVariantSchema = forwardRef<InputVariantSchemaRef, InputVariantSchemaProps>(
  function InputVariantSchema(
    {
      disabled = false,
      value = { ranges: [], saltBase: '' },
      onChange,
      saltBaseOptions,
      onSaltBaseChange,
    },
    ref,
  ) {
    const currentType = value.type ?? BUCKET_TYPE.simple;
    const normalizedRanges = useMemo(
      () =>
        currentType === BUCKET_TYPE.advanced
          ? value.ranges.map(sanitizeRange)
          : normalizeRanges(value.ranges),
      [currentType, value.ranges],
    );
    const displayRanges = useMemo(
      () =>
        currentType === BUCKET_TYPE.advanced && normalizedRanges.length > 0
          ? fillGapsAsRanges(normalizedRanges)
          : normalizedRanges,
      [currentType, normalizedRanges],
    );
    const activeBuckets = useMemo(() => rangesToBuckets(displayRanges), [displayRanges]);
    const activeBucketsSet = useMemo(() => new Set(activeBuckets), [activeBuckets]);
    const rangeColorByBucket = useMemo(() => {
      const colorMap = new Map<number, string>();
      if (currentType !== BUCKET_TYPE.advanced) return colorMap;
      displayRanges.forEach((range, index) => {
        const color = getRangeColor(index);
        for (let bucket = range.from; bucket <= range.to; bucket++) colorMap.set(bucket, color);
      });
      return colorMap;
    }, [currentType, displayRanges]);

    /** First click of a range selection — null when no selection is in progress */
    const [rangeStart, setRangeStart] = useState<number | null>(null);
    /** Bucket currently under the pointer while a range selection is in progress */
    const [hoverBucket, setHoverBucket] = useState<number | null>(null);

    /**
     * ---
     * Drag-select state
     * ---
     */

    /** True while the primary mouse button is held down */
    const isMouseDownRef = useRef(false);
    /** Buckets visited during the current drag (empty when not dragging) */
    const dragBucketsRef = useRef(new Set<number>());
    const [dragBuckets, setDragBuckets] = useState<Set<number>>(new Set());

    /** Refs so the global mouseup effect never needs to be re-registered */
    const activeBucketsRef = useRef(activeBuckets);
    activeBucketsRef.current = activeBuckets;
    const rangeStartRef = useRef(rangeStart);
    rangeStartRef.current = rangeStart;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const valueRef = useRef(value);
    valueRef.current = value;
    const typeRef = useRef(currentType);
    typeRef.current = currentType;

    /**
     * ---
     * Derived preview buckets (shown while user is mid-selection)
     * ---
     */

    const previewBuckets = useMemo<Set<number>>(() => {
      // Drag in progress → preview exactly the visited buckets
      if (dragBuckets.size > 0) return dragBuckets;
      // Click-range in progress → preview the rectangular range
      if (rangeStart === null || hoverBucket === null) return new Set();
      const rangeMin = Math.min(rangeStart, hoverBucket);
      const rangeMax = Math.max(rangeStart, hoverBucket);
      const previewSet = new Set<number>();
      for (let bucketIndex = rangeMin; bucketIndex <= rangeMax; bucketIndex++)
        previewSet.add(bucketIndex);
      return previewSet;
    }, [dragBuckets, rangeStart, hoverBucket]);

    /**
     * ---
     * Grid interaction
     * ---
     */

    const handleBucketMouseDown = useCallback((bucket: number) => {
      isMouseDownRef.current = true;
      const initialDragSet = new Set<number>([bucket]);
      dragBucketsRef.current = initialDragSet;
      setDragBuckets(new Set(initialDragSet));
    }, []);

    const handleBucketMouseEnter = useCallback(
      (bucket: number) => {
        if (isMouseDownRef.current) {
          // Drag mode — accumulate individual buckets
          const updatedDragSet = new Set([...dragBucketsRef.current, bucket]);
          dragBucketsRef.current = updatedDragSet;
          setDragBuckets(new Set(updatedDragSet));
        } else if (rangeStart !== null) {
          // Click-range mode — update hover for preview
          setHoverBucket(bucket);
        }
      },
      [rangeStart],
    );

    const cancelRange = useCallback(() => {
      setRangeStart(null);
      setHoverBucket(null);
      isMouseDownRef.current = false;
      dragBucketsRef.current = new Set();
      setDragBuckets(new Set());
    }, []);

    useImperativeHandle(ref, () => ({ cancelRange }), [cancelRange]);

    /**
     * ---
     * Shortcut handlers
     * ---
     */

    const [randomRangeCount, setRandomRangeCount] = useState(2);
    const [saltLocked, setSaltLocked] = useState(() => !!value.salt);
    const [simpleRangeCount, setSimpleRangeCount] = useState<number | undefined>(() => {
      const initialCount =
        currentType === BUCKET_TYPE.advanced ? displayRanges.length : value.ranges.length;
      return initialCount > 0 ? initialCount : undefined;
    });
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(currentType === BUCKET_TYPE.advanced);
    const saltBaseModel = useMemo(
      () =>
        (saltBaseOptions ?? []).map((option) => ({
          key: option.attributeName,
          value: option.attributeName,
          item: (
            <span className="flex w-full items-center justify-between gap-1">
              <span className="grid">
                <span title={option.attributeName} className="truncate">
                  {option.attributeName}
                </span>
              </span>
              {option.codeDataType && (
                <Badge className="shrink-0 p-0 px-1.5 text-[10px]" variant="success">
                  {option.codeDataType}
                </Badge>
              )}
            </span>
          ),
        })),
      [saltBaseOptions],
    );

    useEffect(() => {
      const nextCount =
        currentType === BUCKET_TYPE.advanced ? displayRanges.length : value.ranges.length;
      setSimpleRangeCount(nextCount > 0 ? nextCount : undefined);
    }, [currentType, displayRanges.length, value.ranges]);

    useEffect(() => {
      setIsAdvancedOpen(currentType === BUCKET_TYPE.advanced);
    }, [currentType]);

    const handleSelectHalf = useCallback(() => {
      onChange({
        ...valueRef.current,
        type: BUCKET_TYPE.advanced,
        ranges: buildSimpleRanges(2),
      });
      cancelRange();
    }, [onChange, cancelRange]);

    const handleSelectThird = useCallback(() => {
      onChange({
        ...valueRef.current,
        type: BUCKET_TYPE.advanced,
        ranges: buildSimpleRanges(3),
      });
      cancelRange();
    }, [onChange, cancelRange]);

    const handleSelectQuarter = useCallback(() => {
      onChange({
        ...valueRef.current,
        type: BUCKET_TYPE.advanced,
        ranges: buildSimpleRanges(4),
      });
      cancelRange();
    }, [onChange, cancelRange]);

    const handleSelectFifth = useCallback(() => {
      onChange({
        ...valueRef.current,
        type: BUCKET_TYPE.advanced,
        ranges: buildSimpleRanges(5),
      });
      cancelRange();
    }, [onChange, cancelRange]);

    const handleSelectRandom = useCallback(() => {
      onChange({
        ...valueRef.current,
        type: BUCKET_TYPE.advanced,
        ranges: buildRandomRanges(randomRangeCount),
      });
      cancelRange();
    }, [randomRangeCount, onChange, cancelRange]);

    /**
     * ---
     * Global mouseup — commits drag or single-click
     * ---
     */

    useEffect(() => {
      const handleMouseUp = () => {
        if (!isMouseDownRef.current) return;
        isMouseDownRef.current = false;

        const dragSet = dragBucketsRef.current;
        const currentActive = activeBucketsRef.current;

        const commitSelectedRange = (selectedRange: BucketRange) => {
          if (typeRef.current === BUCKET_TYPE.advanced) {
            const nextRanges = applyAdvancedSelectedRange(valueRef.current.ranges, selectedRange);
            onChangeRef.current({
              ...valueRef.current,
              type: BUCKET_TYPE.advanced,
              ranges: nextRanges,
            });
            return;
          }
          const rangeSet = new Set<number>();
          for (let bucket = selectedRange.from; bucket <= selectedRange.to; bucket++) {
            rangeSet.add(bucket);
          }
          const merged = [...new Set([...currentActive, ...rangeSet])];
          onChangeRef.current({
            ...valueRef.current,
            ranges: bucketsToRanges(merged),
          });
        };

        if (dragSet.size > 1) {
          // Drag mode: commit a contiguous range from min to max visited bucket.
          const dragBuckets = [...dragSet];
          const from = Math.min(...dragBuckets);
          const to = Math.max(...dragBuckets);
          commitSelectedRange({ from, to });
          setRangeStart(null);
          setHoverBucket(null);
        } else if (dragSet.size === 1) {
          // Single click — existing two-click range behavior
          const [clickedBucket] = dragSet;
          const currentRangeStart = rangeStartRef.current;
          if (currentRangeStart === null) {
            setRangeStart(clickedBucket);
            setHoverBucket(clickedBucket);
          } else {
            commitSelectedRange({
              from: Math.min(currentRangeStart, clickedBucket),
              to: Math.max(currentRangeStart, clickedBucket),
            });
            setRangeStart(null);
            setHoverBucket(null);
          }
        }

        dragBucketsRef.current = new Set();
        setDragBuckets(new Set());
      };

      document.addEventListener('mouseup', handleMouseUp);
      return () => document.removeEventListener('mouseup', handleMouseUp);
    }, []);

    return (
      <div className="flex w-full flex-col gap-6">
        {/* Seed form */}
        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <LabelWithoutRef>Semilla de segmentación</LabelWithoutRef>
            <p className="text-[0.75rem] text-muted-foreground">(Fact + Salt)</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger tabIndex={-1}>
                  <InfoCircledIcon className="size-4 text-accent" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[20rem] space-y-2 p-4 text-muted-foreground">
                  <p>Valor para asegurar una segmentación de usuarios determinista.</p>
                  <p>Cambiar el fact o el salt redistribuirá a los usuarios en nuevos segmentos.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            {value.saltBase &&
              (saltBaseModel.length > 0 && onSaltBaseChange ? (
                <ComboboxModel
                  id="segment-salt1"
                  className="w-full md:w-[15rem]"
                  disabled={disabled}
                  model={saltBaseModel}
                  placeholder="Seleccionar fact"
                  size="xs"
                  value={value.saltBase}
                  variant="outline"
                  onSelect={onSaltBaseChange}
                />
              ) : (
                <Input
                  id="segment-salt1"
                  className="w-full md:w-[15rem]"
                  disabled
                  value={value.saltBase}
                />
              ))}
            <div className="relative flex-1">
              <InputId
                button={{ label: 'Auto generar' }}
                counter={{ enable: true }}
                disabled={disabled || saltLocked}
                id="segment-salt2"
                maxLength={50}
                placeholder="Salt (ej. high-conversion seed, 1a05f...)"
                value={value.salt ?? ''}
                onChange={(e) =>
                  onChange({
                    ...value,
                    salt: e.target.value.trim().replace(/\s+/g, '_') || undefined,
                  })
                }
              />
              <button
                aria-label={saltLocked ? 'Desbloquear semilla' : 'Bloquear semilla'}
                className={cn(
                  'absolute -top-2.5 -right-3 z-10 flex size-6 items-center justify-center rounded-full transition-colors duration-200 ease-in-out',
                  saltLocked
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
                title={saltLocked ? 'Desbloquear semilla' : 'Bloquear semilla'}
                type="button"
                onClick={() => setSaltLocked((prev) => !prev)}
              >
                {saltLocked ? (
                  <LockClosedIcon className="size-3" />
                ) : (
                  <LockOpen1Icon className="size-3" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Simple selection of variants */}
        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <LabelWithoutRef>Variantes</LabelWithoutRef>
            <p className="text-[0.75rem] text-muted-foreground">
              (mínimo {MIN_SIMPLE_RANGES} - máximo {MAX_SIMPLE_RANGES})
            </p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger tabIndex={-1}>
                  <InfoCircledIcon className="size-4 text-accent" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[20rem] space-y-2 p-4 text-muted-foreground">
                  <p>El tamaño de las variantes se ajusta automáticamente.</p>
                  <p>
                    Si no son exactamente divisibles, solo la última variante ajusta la diferencia.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <InputNumber
            id="variants-input"
            disabled={disabled}
            max={MAX_SIMPLE_RANGES}
            min={currentType === BUCKET_TYPE.advanced ? MIN_ADVANCED_RANGES : MIN_SIMPLE_RANGES}
            value={simpleRangeCount}
            onValueChange={(valueNumber) => {
              if (valueNumber === undefined) {
                setSimpleRangeCount(undefined);
                return;
              }

              const nextCount = clampRangeCount(valueNumber);
              // Ignore programmatic sync updates from grid/shortcuts; only repartition
              // buckets when the user actually changes the input value.
              if (nextCount === simpleRangeCount) return;

              setSimpleRangeCount(nextCount);
              onChange({
                ...value,
                type:
                  currentType === BUCKET_TYPE.advanced ? BUCKET_TYPE.advanced : BUCKET_TYPE.simple,
                ranges: buildSimpleRanges(nextCount),
              });
            }}
          />
        </div>

        {/* Advanced selection of variants */}
        <Collapsible
          className="[&[data-state=open]>div>button>svg.chevron]:rotate-90"
          open={isAdvancedOpen}
          onOpenChange={(open) => {
            setIsAdvancedOpen(open);
            if (open) {
              const nextRanges =
                value.ranges.length > 0 ? fillGapsAsRanges(value.ranges.map(sanitizeRange)) : [];
              onChange({ ...value, type: BUCKET_TYPE.advanced, ranges: nextRanges });
              return;
            }

            onChange({
              ...value,
              type: BUCKET_TYPE.simple,
              ranges: buildSimpleRanges(
                simpleRangeCount ??
                  clampRangeCount(
                    (currentType === BUCKET_TYPE.advanced
                      ? displayRanges.length
                      : value.ranges.length) || MIN_SIMPLE_RANGES,
                  ),
              ),
            });
            cancelRange();
          }}
        >
          <div className="flex justify-end">
            <CollapsibleTrigger asChild>
              <Button className="flex-none" variant="secondary" size="sm">
                <span>Configuración avanzada</span>
                <ChevronRightIcon className="chevron transition-transform duration-200" />
              </Button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent className="grid gap-6 pt-6">
            {/* Instructions */}
            <p className="text-xs leading-relaxed text-pretty">
              <span className="font-bold text-accent">Haz clic</span> en un bucket para iniciar una
              variante. Luego selecciona otro para completarla
              <br />
              <span className="font-bold text-accent">o haz clic y arrastra</span> para seleccionar
              varios buckets rápidamente.
            </p>

            <div className="flex gap-8 max-sm:flex-col">
              {/* Shortcuts */}
              <div className="flex-1 space-y-1.5">
                <LabelWithoutRef className="text-[0.75rem] text-muted-foreground">
                  Selección rápida
                </LabelWithoutRef>

                {/* Clean */}
                <Button
                  className="w-full"
                  disabled={disabled || activeBuckets.length === 0}
                  size="sm"
                  title="Limpiar selección actual de buckets"
                  variant="outline"
                  onClick={() => {
                    onChange({
                      ...value,
                      ranges: bucketsToRanges([]),
                    });
                    cancelRange();
                  }}
                >
                  Limpiar
                </Button>

                {/* Preset variants */}
                <ButtonGroup className="w-full">
                  <Button
                    className="flex-1"
                    disabled={disabled}
                    size="sm"
                    title="Generar 2 variantes del 50%"
                    variant="outline"
                    onClick={handleSelectHalf}
                  >
                    2
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={disabled}
                    size="sm"
                    title="Generar 3 variantes del 33% aproximadamente"
                    variant="outline"
                    onClick={handleSelectThird}
                  >
                    3
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={disabled}
                    size="sm"
                    title="Generar 4 variantes del 25%"
                    variant="outline"
                    onClick={handleSelectQuarter}
                  >
                    4
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={disabled}
                    size="sm"
                    title="Generar 5 variantes del 20%"
                    variant="outline"
                    onClick={handleSelectFifth}
                  >
                    5
                  </Button>
                </ButtonGroup>

                <Separator className="my-4!" orientation="horizontal" />

                {/* Random */}
                <div className="grid gap-1">
                  <LabelWithoutRef className="text-[0.75rem] text-muted-foreground">
                    Variantes aleatorias
                  </LabelWithoutRef>
                  <SliderWithBadge
                    disabled={disabled}
                    max={MAX_SIMPLE_RANGES}
                    min={MIN_SIMPLE_RANGES}
                    value={randomRangeCount}
                    onValueChange={(valueNumber) =>
                      setRandomRangeCount(clampRangeCount(valueNumber))
                    }
                  />
                  <Button
                    className="w-full"
                    disabled={disabled}
                    size="sm"
                    title={`Generar ${randomRangeCount} variantes con longitudes aleatorias`}
                    variant="outline"
                    onClick={handleSelectRandom}
                  >
                    Probar
                  </Button>
                </div>
              </div>

              {/* Grid 10x10 */}
              <div className="m-auto inline-block rounded-[1.2rem] border border-input p-4 shadow-xs">
                <div className="relative size-[320px]">
                  {/* Vertical divider — between cols 5 and 6 */}
                  <div className="pointer-events-none absolute inset-y-0 left-1/2 ml-[1px] w-0 -translate-x-px border-l border-dotted border-muted" />
                  {/* Horizontal divider — between rows 5 and 6 */}
                  <div className="pointer-events-none absolute inset-x-0 top-1/2 mt-[1px] h-0 -translate-y-px border-t border-dotted border-muted" />
                  {/* Each cell is a 5×5 quadrant */}
                  <div className="grid grid-cols-2 gap-4">
                    {GRID_QUADRANT_ORDER.map((quadrantIndex) => {
                      return (
                        <div
                          key={quadrantIndex}
                          className="grid grid-cols-5 justify-items-center gap-2"
                        >
                          {QUADRANTS[quadrantIndex].map((bucketIndex) => (
                            <BucketButton
                              key={bucketIndex}
                              disabled={disabled}
                              index={bucketIndex}
                              isActive={activeBucketsSet.has(bucketIndex)}
                              isPreview={previewBuckets.has(bucketIndex)}
                              rangeColor={rangeColorByBucket.get(bucketIndex)}
                              isStart={rangeStart === bucketIndex}
                              onMouseDown={handleBucketMouseDown}
                              onMouseEnter={handleBucketMouseEnter}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  },
);

InputVariantSchema.displayName = 'InputVariantSchema';
